import { useEffect, useState, type JSX } from 'react'
import { Check, ListChecks, Loader2, PencilLine, X } from 'lucide-react'
import { cn } from '../../../utils/cn.js'
import { useConversationChecklistTab, type ChecklistTemplateItem } from '../../../hooks/useConversationChecklistTab.js'
import { Field, GhostButton, PrimaryButton, R, SelectField, textareaClass } from './primitives.js'

interface ChecklistTabProps {
  conversationId: string
  canEdit: boolean
  onChanged: () => void
}

export default function ChecklistTab({ conversationId, canEdit, onChanged }: ChecklistTabProps): JSX.Element {
  const { assignment, items, available, loading, assigning, updatingItemId, error, assignChecklist, toggleItemDone, saveRemarks, fetchTemplateItems, refresh } = useConversationChecklistTab(conversationId)
  const [showAssignDialog, setShowAssignDialog] = useState(false)
  const completedCount = items.filter((i) => i.isDone).length
  const totalCount = items.length
  const progressPct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0

  const handleAssign = async (checklistId: string): Promise<boolean> => {
    const ok = await assignChecklist(checklistId)
    if (ok) onChanged()
    return ok
  }

  if (loading) return <div className="flex h-full items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
  if (error) return <div className="flex h-full flex-col items-center justify-center gap-3"><p className="text-sm text-red-500">{error}</p><GhostButton onClick={refresh}>Retry</GhostButton></div>

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col">
        {assignment ? (
          <>
            <div className="shrink-0 border-b border-slate-100 px-5 py-3.5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-bold text-slate-800">{assignment.checklistName}</h3>
                  {assignment.checklistDescription && <p className="mt-0.5 text-xs text-slate-500">{assignment.checklistDescription}</p>}
                </div>
                {canEdit && (
                  <GhostButton onClick={() => setShowAssignDialog(true)} disabled={assigning} className="!px-3 !py-1.5">
                    <span className="flex items-center gap-1.5 text-xs">
                      {assigning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ListChecks className="h-3.5 w-3.5" />}
                      Change Checklist
                    </span>
                  </GhostButton>
                )}
              </div>
              <div className="mt-3 flex items-center gap-3">
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full rounded-full bg-[#3366FF] transition-all duration-300" style={{ width: `${progressPct}%` }} />
                </div>
                <span className="shrink-0 text-xs font-semibold text-slate-500">{completedCount}/{totalCount} done</span>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
              <table className="w-full">
                <thead className="sticky top-0 z-10 bg-slate-50/95 backdrop-blur">
                  <tr className="border-b border-slate-200 text-left text-[11px] font-bold uppercase tracking-wide text-slate-400">
                    <th className="w-8 px-4 py-2.5">#</th>
                    <th className="w-10 px-2 py-2.5 text-center">Done</th>
                    <th className="px-2 py-2.5">Item</th>
                    <th className="px-2 py-2.5">Remarks</th>
                    {canEdit && <th className="w-10 px-3 py-2.5 text-right">Edit</th>}
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <ChecklistItemRow key={item.id} item={item} canEdit={canEdit} updating={updatingItemId === item.id} onToggle={() => void toggleItemDone(item.id, !item.isDone)} onSaveRemarks={async (r, markDone) => { await saveRemarks(item.id, r, markDone) }} />
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-blue-50 text-[#3366FF]/70">
              <ListChecks className="h-9 w-9" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-500">No checklist assigned</p>
              <p className="mt-1 max-w-xs text-xs text-slate-400">Assign a checklist to track verification steps for this conversation.</p>
            </div>
            {canEdit && (
              <PrimaryButton onClick={() => setShowAssignDialog(true)} disabled={assigning || available.length === 0}>
                {available.length === 0 ? 'No checklists available' : <span className="flex items-center gap-2"><ListChecks className="h-4 w-4" /> Assign Checklist</span>}
              </PrimaryButton>
            )}
          </div>
        )}
      </div>

      {showAssignDialog && (
        <AssignChecklistDialog
          available={available}
          currentChecklistId={assignment?.checklistId ?? null}
          assigning={assigning}
          fetchTemplateItems={fetchTemplateItems}
          onAssign={handleAssign}
          onClose={() => setShowAssignDialog(false)}
        />
      )}
    </>
  )
}

function ChecklistItemRow({ item, canEdit, updating, onToggle, onSaveRemarks }: {
  item: { id: string; slNo: number; itemName: string; description: string | null; isDone: boolean; remarks: string | null }
  canEdit: boolean; updating: boolean; onToggle: () => void; onSaveRemarks: (r: string | null, markDone?: boolean) => Promise<void>
}): JSX.Element {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(item.remarks ?? '')
  const [saving, setSaving] = useState(false)
  const [pendingCheck, setPendingCheck] = useState(false)

  useEffect(() => { setDraft(item.remarks ?? '') }, [item.remarks])

  const handleCheck = () => {
    if (item.isDone) {
      // Unchecking — just toggle directly
      onToggle()
    } else {
      // Checking — open remark input, don't save done yet
      setPendingCheck(true)
      setEditing(true)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    await onSaveRemarks(draft.trim() || null, pendingCheck || undefined)
    setSaving(false)
    setEditing(false)
    setPendingCheck(false)
  }
  const handleCancel = () => {
    setDraft(item.remarks ?? '')
    setEditing(false)
    setPendingCheck(false)
  }

  return (
    <tr className={cn('border-b border-slate-50 transition-colors hover:bg-slate-50/40', (item.isDone || pendingCheck) && 'bg-emerald-50/30')}>
      <td className="px-4 py-3 text-xs font-semibold text-slate-400">{item.slNo}</td>
      <td className="px-2 py-3 text-center">
        <button type="button" disabled={!canEdit || updating} onClick={handleCheck}
          className={cn('flex h-5 w-5 items-center justify-center border transition-all', R,
            (item.isDone || pendingCheck) ? 'border-[#3366FF] bg-[#3366FF] text-white' : 'border-slate-300 bg-white text-transparent hover:border-[#3366FF]',
            (!canEdit || updating) && 'cursor-not-allowed opacity-60')}>
          {(item.isDone || pendingCheck) && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
        </button>
      </td>
      <td className="px-2 py-3">
        <div className={cn('text-sm font-medium', (item.isDone || pendingCheck) ? 'text-slate-400 line-through' : 'text-slate-800')}>{item.itemName}</div>
        {item.description && <div className="mt-0.5 text-xs text-slate-400">{item.description}</div>}
      </td>
      <td className="px-2 py-3">
        {editing ? (
          <div className="flex flex-col gap-1.5">
            <textarea autoFocus rows={2} value={draft} onChange={(e) => setDraft(e.target.value)} className={textareaClass} placeholder="Add remarks…" />
            <div className="flex gap-2">
              <button type="button" onClick={handleSave} disabled={saving} className="flex items-center gap-1 text-xs font-semibold text-[#3366FF] hover:text-[#2952CC] disabled:opacity-50">
                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Save
              </button>
              <button type="button" onClick={handleCancel} className="flex items-center gap-1 text-xs font-semibold text-slate-400 hover:text-slate-600">
                <X className="h-3 w-3" /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className={cn('cursor-text text-xs', item.remarks ? 'text-slate-600' : 'text-slate-300')} onClick={() => canEdit && setEditing(true)}>
            {item.remarks || (canEdit ? 'Click to add remarks…' : '—')}
          </div>
        )}
      </td>
      {canEdit && (
        <td className="px-3 py-3 text-right">
          {canEdit && !editing && (
            <button type="button" onClick={() => setEditing(true)} className="inline-flex items-center justify-center text-slate-300 transition-colors hover:text-[#3366FF]" title="Edit remarks">
              <PencilLine className="h-3.5 w-3.5" />
            </button>
          )}
        </td>
      )}
    </tr>
  )
}

function AssignChecklistDialog({
  available,
  currentChecklistId,
  assigning,
  fetchTemplateItems,
  onAssign,
  onClose,
}: {
  available: { id: string; name: string }[]
  currentChecklistId: string | null
  assigning: boolean
  fetchTemplateItems: (checklistId: string) => Promise<ChecklistTemplateItem[]>
  onAssign: (checklistId: string) => Promise<boolean>
  onClose: () => void
}): JSX.Element {
  const [selectedId, setSelectedId] = useState(currentChecklistId ?? '')
  const [previewItems, setPreviewItems] = useState<ChecklistTemplateItem[]>([])
  const [loadingPreview, setLoadingPreview] = useState(false)

  const options = available.map((c) => ({ value: c.id, label: c.name }))

  useEffect(() => {
    if (!currentChecklistId) return
    void (async () => {
      setLoadingPreview(true)
      const items = await fetchTemplateItems(currentChecklistId)
      setPreviewItems(items)
      setLoadingPreview(false)
    })()
  }, [currentChecklistId, fetchTemplateItems])

  const handleSelect = async (id: string): Promise<void> => {
    setSelectedId(id)
    if (!id) {
      setPreviewItems([])
      return
    }
    setLoadingPreview(true)
    const items = await fetchTemplateItems(id)
    setPreviewItems(items)
    setLoadingPreview(false)
  }

  const handleAssign = async (): Promise<void> => {
    if (!selectedId) return
    const ok = await onAssign(selectedId)
    if (ok) onClose()
  }

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm" onClick={onClose} />
      <div className={cn('animate-slide-in-right fixed right-0 top-0 z-50 flex h-full w-[680px] flex-col bg-[#F8FAFC] shadow-2xl')}>
        <div className="flex shrink-0 items-center justify-between border-b border-slate-200 bg-white px-5 py-3.5">
          <h3 className="text-sm font-bold text-slate-800">
            {currentChecklistId ? 'Change Checklist' : 'Assign Checklist'}
          </h3>
          <button type="button" onClick={onClose} className="text-slate-400 transition-colors hover:text-slate-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-5">
          <Field label="Select Checklist" required>
            <SelectField
              icon={<ListChecks className="h-4 w-4" />}
              value={selectedId}
              onChange={(v) => void handleSelect(v)}
              placeholder="Select a checklist"
              options={options}
              loading={false}
            />
          </Field>

          {selectedId && (
            <div className="mt-5">
              <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Checklist Items Preview</h4>
              {loadingPreview ? (
                <div className="flex items-center justify-center py-8 text-slate-400">
                  <Loader2 className="h-5 w-5 animate-spin" />
                </div>
              ) : previewItems.length === 0 ? (
                <div className={cn('py-8 text-center text-xs text-slate-400', R, 'border border-slate-200 bg-white')}>
                  No items in this checklist
                </div>
              ) : (
                <div className={cn('overflow-hidden border border-slate-200 bg-white', R)}>
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50 text-left text-[11px] font-bold uppercase tracking-wide text-slate-400">
                        <th className="w-16 px-3 py-2">Sl No</th>
                        <th className="px-3 py-2">Item Name</th>
                        <th className="px-3 py-2">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewItems.map((item) => (
                        <tr key={item.id} className="border-b border-slate-50 last:border-0">
                          <td className="px-3 py-2.5 text-xs font-semibold text-slate-400">{item.slNo}</td>
                          <td className="px-3 py-2.5 text-sm font-medium text-slate-800">{item.itemName}</td>
                          <td className="px-3 py-2.5 text-xs text-slate-500">{item.description || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-slate-200 bg-white px-5 py-3.5">
          <GhostButton onClick={onClose}>Cancel</GhostButton>
          <PrimaryButton onClick={() => void handleAssign()} disabled={!selectedId || previewItems.length === 0 || assigning || selectedId === currentChecklistId}>
            {assigning ? <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Assigning…</span> : currentChecklistId ? 'Change' : 'Assign'}
          </PrimaryButton>
        </div>
      </div>
    </>
  )
}
